import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type RefObject,
} from "react";
import cx from "classnames";

import * as styles from "./AppShell.module.scss";

type SlotProps = ComponentPropsWithoutRef<"div">;

/** Lets Status write its measured height onto the grid that reads it. */
const ShellContext = createContext<RefObject<HTMLDivElement | null> | null>(
  null,
);

type BottomNavPortalTarget = {
  element: HTMLDivElement | null;
  setElement: (element: HTMLDivElement | null) => void;
};

/** Lets page content portal into the box stacked on the bottom nav. */
const BottomNavPortalContext = createContext<BottomNavPortalTarget | null>(
  null,
);

const Root = ({ className, ...rest }: SlotProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [portal, setPortal] = useState<HTMLDivElement | null>(null);

  return (
    <ShellContext value={ref}>
      <BottomNavPortalContext
        value={{ element: portal, setElement: setPortal }}
      >
        <div ref={ref} className={cx(styles.shell, className)} {...rest} />
      </BottomNavPortalContext>
    </ShellContext>
  );
};

const Stream = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.stream, className)} {...rest} />
);

/**
 * The status message. It sits above the rail, the main column and the dock,
 * spanning every column, and it is the one measured box in the Shell: it is
 * fixed so it never scrolls away, and a fixed box reserves no space, so the
 * grid row holding its place and every sticky offset below it need its height.
 * CSS cannot carry one element's height into another's `top` across formatting
 * contexts, and the copy runs to between one and four lines, so there is no
 * token to hard-code.
 */
const Status = ({ className, ...rest }: SlotProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const shellRef = useContext(ShellContext);

  useEffect(() => {
    const element = ref.current;
    const shell = shellRef?.current;
    if (!element || !shell) {
      return;
    }

    const observer = new ResizeObserver(() => {
      shell.style.setProperty(
        "--shell-status-height",
        `${element.offsetHeight}px`,
      );
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      shell.style.removeProperty("--shell-status-height");
    };
  }, [shellRef]);

  return <div ref={ref} className={cx(styles.status, className)} {...rest} />;
};

const Body = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.body, className)} {...rest} />
);

const Rail = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.rail, className)} {...rest} />
);

const Main = ({ className, ...rest }: ComponentPropsWithoutRef<"main">) => (
  <main className={cx(styles.main, className)} {...rest} />
);

const TopBar = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.topBar, className)} {...rest} />
);

const Content = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.content, className)} {...rest} />
);

const Footer = ({ className, ...rest }: ComponentPropsWithoutRef<"footer">) => (
  <footer className={cx(styles.footer, className)} {...rest} />
);

const ToastAnchor = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.toastAnchor, className)} {...rest} />
);

const Dock = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.dock, className)} {...rest} />
);

const BottomNav = ({ className, ...rest }: SlotProps) => (
  <div className={cx(styles.bottomNav, className)} {...rest} />
);

/**
 * Whatever page content portals in here rides on top of the bottom nav. It is
 * measured for the same reason Status is: the bottom nav is fixed, so the
 * padding that keeps content clear of it has to grow with it.
 */
const BottomNavPortal = ({ className, ...rest }: SlotProps) => {
  const shellRef = useContext(ShellContext);
  const portal = useContext(BottomNavPortalContext);
  const element = portal?.element;

  useEffect(() => {
    const shell = shellRef?.current;
    if (!element || !shell) {
      return;
    }

    const observer = new ResizeObserver(() => {
      shell.style.setProperty(
        "--shell-bottom-nav-portal-height",
        `${element.offsetHeight}px`,
      );
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      shell.style.removeProperty("--shell-bottom-nav-portal-height");
    };
  }, [shellRef, element]);

  return (
    <div
      ref={portal?.setElement}
      className={cx(styles.bottomNavPortal, className)}
      {...rest}
    />
  );
};

const useBottomNavPortal = () =>
  useContext(BottomNavPortalContext)?.element ?? null;

const AppShell = {
  Root,
  Stream,
  Status,
  Body,
  Rail,
  Main,
  TopBar,
  Content,
  Footer,
  ToastAnchor,
  Dock,
  BottomNav,
  BottomNavPortal,
};

export { AppShell, useBottomNavPortal };
